// ===============================================================
//  Generator list + "find similar" explorer
//  --------------------------------------------------------------
//  • shows /teams/{teamId}/generators
//  • lets the user search nearby *look-alike* facilities via
//        GET  /api/similar?address=…&radiusMiles=…&keyword=…
//  • NEW ⬅︎ "Whole-State" mode in the modal:
//        – choose radio "Within this state"
//        – enter state (abbr. or full) & keyword
//        – hits come from server's new ?state=… endpoint
//  • dedupes anything already stored in Firestore when "Exclude existing" is on
//  • No client-side Google Maps API key needed - all via server proxy
// ===============================================================

import React, { useState, useEffect, useContext } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import {
  FaSearch,
  FaSpinner,
  FaMapMarkerAlt,
  FaBuilding,
  FaFilter,
  FaEye,
  FaTrash,
  FaGlobeAmericas,
  FaExternalLinkAlt,
  FaPhone,
  FaGlobe,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { db } from "../../firebase";
import { AuthContext } from "../../AuthProvider";
import { getAuth } from "firebase/auth";

// ---------------------------------------------------------------------------
// CONSTANTS
// ---------------------------------------------------------------------------
const ADMIN_UIDS = [
  "wVdZ4zMt1PYD6mmNflk6ZApiZEe2",
  "LRPoMCWtpFRT0tcK2bTi90HbVSZ2",
];
const DEFAULT_FETCH_LIMIT = 100;
const ENABLE_LOGGING = false;

const log = (...a) => ENABLE_LOGGING && console.log("[Generators]", ...a);

const badgeClass = (type) => {
  switch (type) {
    case "Broker":
      return "bg-blue-100 text-blue-800";
    case "Direct":
      return "bg-green-100 text-green-800";
    case "Internalized":
      return "bg-purple-100 text-purple-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

// =============================================================
//                       COMPONENT
// =============================================================
const Generators = ({ teamId }) => {
  const { user } = useContext(AuthContext);
  const isAdmin = user && ADMIN_UIDS.includes(user.uid);

  // Firestore data ------------------------------------------------
  const [generators, setGenerators] = useState([]);
  const [remoteDocs, setRemoteDocs] = useState([]);
  const [filtered, setFiltered] = useState([]);

  // UI state ------------------------------------------------------
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [filterBroker, setFilterBroker] = useState("");

  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selected, setSelected] = useState(null);

  // "find similar" state -----------------------------------------
  const [modeState, setModeState] = useState("nearby"); // NEW ⬅︎ radio
  const [radius, setRadius] = useState(100); // miles
  const [kw, setKw] = useState("");
  const [usState, setUsState] = useState(""); // NEW ⬅︎
  const [excludeOwned, setExclude] = useState(true);
  const [loadingSim, setLoadingSim] = useState(false);
  const [similarSites, setSimilarSites] = useState([]);
  const [sourceData, setSourceData] = useState(null); // geocoded location

  // ------------------------------------------------------------------
  // Initial Firestore pull
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!user || !teamId) return;
    const run = async () => {
      setLoading(true);
      try {
        const ref = collection(db, "teams", teamId, "generators");
        const snap = await getDocs(
          query(ref, orderBy("customerName"), limit(DEFAULT_FETCH_LIMIT))
        );
        const rows = [];
        snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
        setGenerators(rows);
        setFiltered(rows);
        toast.success(`Loaded ${rows.length} generators`);
      } catch (e) {
        console.error(e);
        toast.error("Load failed");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [user, teamId]);

  // ------------------------------------------------------------------
  // Reactive "type-ahead" lookup (array-contains ≤20 chars / single token)
  // ------------------------------------------------------------------
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term || term.includes(" ") || term.length > 20) {
      setRemoteDocs([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const ref = collection(db, "teams", teamId, "generators");
        const snap = await getDocs(
          query(ref, where("searchArray", "array-contains", term), limit(200))
        );
        const rows = [];
        snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
        if (!cancelled) setRemoteDocs(rows);
      } catch (e) {
        log("reactive lookup fail", e);
      }
    };
    run();
    return () => (cancelled = true);
  }, [searchTerm, teamId]);

  // ------------------------------------------------------------------
  // Manual search
  // ------------------------------------------------------------------
  const manualSearch = async () => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return toast.info("Enter a search term");
    setSearching(true);
    try {
      const ref = collection(db, "teams", teamId, "generators");
      const snap = await getDocs(
        query(ref, where("searchArray", "array-contains", term), limit(500))
      );
      const rows = [];
      snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
      setRemoteDocs(rows);
      toast.info(`Found ${rows.length} remote hits`);
    } catch (e) {
      console.error(e);
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  // ------------------------------------------------------------------
  // Merge docs + apply filters
  // ------------------------------------------------------------------
  useEffect(() => {
    let rows = [...generators, ...remoteDocs];
    const seen = new Set();
    rows = rows.filter((g) => {
      if (seen.has(g.id)) return false;
      seen.add(g.id);
      return true;
    });

    const term = searchTerm.toLowerCase();
    if (term) {
      rows = rows.filter((g) => {
        const city = g.location?.city || "";
        const state = g.location?.state || "";
        return (
          (g.generatorName || "").toLowerCase().includes(term) ||
          (g.customerName || "").toLowerCase().includes(term) ||
          (g.brokerName || "").toLowerCase().includes(term) ||
          city.toLowerCase().includes(term) ||
          state.toLowerCase().includes(term) ||
          (g.location?.zipCode || "").includes(term)
        );
      });
    }

    if (filterType) rows = rows.filter((g) => g.type === filterType);
    if (filterState)
      rows = rows.filter((g) => g.filters?.state === filterState);
    if (filterCity) rows = rows.filter((g) => g.location?.city === filterCity);
    if (filterBroker) rows = rows.filter((g) => g.brokerName === filterBroker);

    setFiltered(rows);
    setCurrentPage(1);
  }, [
    searchTerm,
    filterType,
    filterState,
    filterCity,
    filterBroker,
    generators,
    remoteDocs,
  ]);

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------
  const buildFullAddress = (g) => {
    if (g.location?.full) return g.location.full;
    const {
      street = "",
      city = "",
      state = "",
      zipCode = "",
    } = g.location || {};
    return `${street}, ${city}, ${state} ${zipCode}`
      .replace(/\s+,/g, "")
      .trim();
  };

  const fetchSimilarSites = async (g) => {
    try {
      setLoadingSim(true);
      setSimilarSites([]);
      setSourceData(null);
      const token = user ? await getAuth().currentUser.getIdToken(true) : null;
      const base =
        window.location.hostname.includes("localhost") ||
        window.location.hostname.includes("127.0.0.1")
          ? "http://127.0.0.1:5001/damia-gcp/us-central1/api"
          : "https://us-central1-damia-gcp.cloudfunctions.net/api";

      /* -------- build querystring -------- */
      const qs = new URLSearchParams(
        modeState === "nearby"
          ? {
              address: buildFullAddress(g),
              radiusMiles: radius,
              keyword: kw,
            }
          : {
              state: usState || g.filters?.state || "",
              keyword: kw || g.customerName || "",
              pageLimit: 3,
            }
      ).toString();

      const r = await fetch(`${base}/similar?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "unknown");

      let sites = data.places || [];
      if (excludeOwned) {
        const owned = new Set(
          generators.map(
            (x) => (x.customerName || "") + (x.location?.city || "")
          )
        );
        sites = sites.filter(
          (p) => !owned.has((p.name || "") + p.formatted_address.split(",")[1])
        );
      }
      setSimilarSites(sites);
      setSourceData(data.source);
      if (sites.length === 0) toast.info("No new sites found");
      else toast.success(`Found ${sites.length} sites`);
    } catch (e) {
      console.error(e);
      toast.error(`Lookup failed – ${e.message}`);
    } finally {
      setLoadingSim(false);
    }
  };

  const openView = (g) => {
    setSelected(g);
    setShowView(true);
    setSimilarSites([]);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterType("");
    setFilterState("");
    setFilterCity("");
    setFilterBroker("");
    setRemoteDocs([]);
    setCurrentPage(1);
  };

  // ------------------------------------------------------------------
  // Derived lists for dropdowns
  // ------------------------------------------------------------------
  const types = ["Broker", "Direct", "Internalized"];
  const states = [
    ...new Set(generators.map((g) => g.filters?.state).filter(Boolean)),
  ].sort();
  const brokers = [
    ...new Set(generators.map((g) => g.brokerName).filter(Boolean)),
  ].sort();
  const cities = [
    ...new Set(
      generators
        .filter((g) => !filterState || g.filters?.state === filterState)
        .map((g) => g.location?.city)
        .filter(Boolean)
    ),
  ].sort();

  // paging -----------------------------------------------------------
  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
  const pageSlice = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const goTo = (p) => p >= 1 && p <= totalPages && setCurrentPage(p);

  // Early return if no team ID
  if (!teamId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="p-4 bg-yellow-50 text-yellow-700 rounded-lg">
          No team selected. Please ensure you have access to a team.
        </div>
      </div>
    );
  }

  // ===========================================================
  //  RENDER
  // ===========================================================
  return (
    <div className="container mx-auto px-4 py-8">
      {/* header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 space-y-4 md:space-y-0">
        <div>
          <h1 className="text-2xl font-bold">Generator Sites</h1>
          <p className="text-gray-600">
            Search &amp; discover similar facilities
          </p>
        </div>
        <div className="text-sm text-gray-500">
          Total&nbsp;{filtered.length}&nbsp;/&nbsp;{generators.length}
        </div>
      </div>

      {/* search + filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="relative mb-4">
          <input
            className="w-full p-3 pl-10 pr-20 border rounded-md focus:ring-2 focus:ring-purple-500"
            placeholder="Search customer, city, state, ZIP…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && manualSearch()}
          />
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <button
            onClick={manualSearch}
            disabled={searching}
            className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400 flex items-center"
          >
            {searching ? <FaSpinner className="animate-spin" /> : "Search"}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="p-2 border rounded-md"
          >
            <option value="">All Types</option>
            {types.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
          <select
            value={filterState}
            onChange={(e) => {
              setFilterState(e.target.value);
              setFilterCity("");
            }}
            className="p-2 border rounded-md"
          >
            <option value="">All States</option>
            {states.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <select
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            className="p-2 border rounded-md"
            disabled={!filterState && cities.length === 0}
          >
            <option value="">All Cities</option>
            {cities.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
          <select
            value={filterBroker}
            onChange={(e) => setFilterBroker(e.target.value)}
            className="p-2 border rounded-md"
          >
            <option value="">All Brokers</option>
            {brokers.map((b) => (
              <option key={b}>{b}</option>
            ))}
          </select>
          <button
            onClick={clearFilters}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded"
          >
            Clear
          </button>
        </div>
      </div>

      {/* table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <FaSpinner className="animate-spin text-2xl text-purple-600 mx-auto mb-2" />
            <p>Loading…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-600">
            <FaFilter className="text-4xl mx-auto mb-2 text-gray-400" />
            <p>No generators match</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    "Type",
                    "Customer/Address",
                    "Generator",
                    "Location",
                    "Broker",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pageSlice.map((g) => (
                  <tr key={g.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${badgeClass(
                          g.type
                        )}`}
                      >
                        {g.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {g.customerName || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {g.generatorName || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-start space-x-1">
                        <FaMapMarkerAlt className="text-gray-400 mt-0.5" />
                        <div className="text-sm">
                          <div className="text-gray-900">
                            {g.location?.city || "—"},{" "}
                            {g.location?.state || "—"}{" "}
                            {g.location?.zipCode || ""}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {g.location?.street || ""}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {g.brokerName ? (
                        <div className="flex items-center space-x-1">
                          <FaBuilding className="text-gray-400" />
                          <span>{g.brokerName}</span>
                        </div>
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap space-x-2">
                      <button
                        onClick={() => openView(g)}
                        className="text-purple-600 hover:text-purple-900"
                      >
                        <FaEye className="inline mr-1" />
                        View
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() =>
                            toast.info("Delete not implemented yet")
                          }
                          className="text-red-600 hover:text-red-800"
                        >
                          <FaTrash className="inline" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* pagination */}
      {filtered.length > 0 && (
        <div className="mt-4 flex justify-center">
          <div className="flex items-center space-x-1">
            <button
              onClick={() => goTo(1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 border rounded ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              First
            </button>
            <button
              onClick={() => goTo(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 border rounded ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              Prev
            </button>
            <span className="px-3">
              {currentPage}/{totalPages}
            </span>
            <button
              onClick={() => goTo(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className={`px-3 py-1 border rounded ${
                currentPage >= totalPages
                  ? "bg-gray-100 text-gray-400"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              Next
            </button>
            <button
              onClick={() => goTo(totalPages)}
              disabled={currentPage >= totalPages}
              className={`px-3 py-1 border rounded ${
                currentPage >= totalPages
                  ? "bg-gray-100 text-gray-400"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              Last
            </button>
          </div>
        </div>
      )}

      {/* -----------------------------------------------------------
           MODAL: generator details + "find similar"
         -----------------------------------------------------------*/}
      {showView && selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[95vh] overflow-hidden shadow-xl">
            {/* header */}
            <div className="bg-gray-50 px-6 py-4 border-b flex justify-between">
              <div>
                <h2 className="text-xl font-bold">{selected.customerName}</h2>
                <p className="text-sm text-gray-600">
                  {selected.generatorName}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowView(false);
                  setSimilarSites([]);
                  setSourceData(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* controls */}
            <div className="px-6 py-3 bg-purple-50 border-b flex flex-wrap gap-3 items-center">
              {/* MODE radio – NEW ⬅︎ */}
              <div className="flex items-center space-x-3 mr-4">
                <label className="flex items-center space-x-1 text-sm">
                  <input
                    type="radio"
                    value="nearby"
                    checked={modeState === "nearby"}
                    onChange={(e) => setModeState(e.target.value)}
                  />
                  <span>Nearby facility</span>
                </label>
                <label className="flex items-center space-x-1 text-sm">
                  <input
                    type="radio"
                    value="state"
                    checked={modeState === "state"}
                    onChange={(e) => setModeState(e.target.value)}
                  />
                  <span>Within this state</span>
                </label>
              </div>

              {/* Keyword */}
              <input
                value={kw}
                onChange={(e) => setKw(e.target.value)}
                className="border rounded px-2 py-1 text-sm"
                placeholder="Keyword (Tesla, etc)"
              />

              {/* radius slider – hide when whole-state */}
              {modeState === "nearby" && (
                <>
                  <input
                    type="range"
                    min={10}
                    max={300}
                    step={10}
                    value={radius}
                    onChange={(e) => setRadius(+e.target.value)}
                  />
                  <span className="text-sm w-16">{radius} mi</span>
                </>
              )}

              {/* State field – only show when state mode */}
              {modeState === "state" && (
                <input
                  value={usState}
                  onChange={(e) => setUsState(e.target.value)}
                  className="border rounded px-2 py-1 text-sm w-24"
                  placeholder="CA"
                />
              )}

              <label className="flex items-center text-sm space-x-1">
                <input
                  type="checkbox"
                  checked={excludeOwned}
                  onChange={(e) => setExclude(e.target.checked)}
                />
                <span>Exclude existing</span>
              </label>
              <button
                onClick={() => fetchSimilarSites(selected)}
                disabled={loadingSim}
                className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded disabled:bg-gray-400"
              >
                {loadingSim ? (
                  <FaSpinner className="animate-spin" />
                ) : (
                  <FaGlobeAmericas />
                )}
                <span>Find Similar</span>
              </button>
              {similarSites.length > 0 && (
                <span className="ml-auto text-sm text-gray-600">
                  {similarSites.length} hits
                </span>
              )}
            </div>

            {/* body */}
            <div className="p-6 overflow-y-auto max-h-[70vh] space-y-6">
              {/* basic info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <section>
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                      Generator Info
                    </h3>
                    <div className="bg-gray-50 rounded p-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Type:</span>
                        <span
                          className={
                            badgeClass(selected.type) +
                            " px-2 py-1 rounded-full text-xs font-semibold"
                          }
                        >
                          {selected.type}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Customer:</span>
                        <span>{selected.customerName || "—"}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Generator:</span>
                        <span>{selected.generatorName || "—"}</span>
                      </div>
                      {selected.brokerName && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Broker:</span>
                          <span>{selected.brokerName}</span>
                        </div>
                      )}
                    </div>
                  </section>
                  <section>
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                      Location
                    </h3>
                    <div className="bg-gray-50 rounded p-3 space-y-2 text-sm">
                      <div className="break-words">
                        {buildFullAddress(selected)}
                      </div>
                      {sourceData && (
                        <div className="text-xs text-gray-500 pt-2 border-t">
                          {sourceData.mode === "nearby" ? (
                            <>
                              Search center: {sourceData.lat.toFixed(4)},{" "}
                              {sourceData.lng.toFixed(4)}
                              <br />
                              Radius:{" "}
                              {Math.round(sourceData.radiusMetres / 1609)} miles
                            </>
                          ) : (
                            <>
                              State-wide search:{" "}
                              {usState || selected.filters?.state || ""}
                              <br />
                              Search radius:{" "}
                              {Math.round(sourceData.radiusMetres / 1609)} miles
                              (auto-calculated)
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </section>
                </div>

                {/* static map preview */}
                <div className="space-y-4">
                  {sourceData && (
                    <section>
                      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                        Map Preview
                      </h3>
                      <div className="bg-gray-100 rounded p-4 text-center">
                        <a
                          href={`https://www.google.com/maps/@${sourceData.lat},${sourceData.lng},12z`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-2 text-purple-600 hover:text-purple-800"
                        >
                          <FaMapMarkerAlt />
                          <span>View Area on Google Maps</span>
                          <FaExternalLinkAlt className="text-xs" />
                        </a>
                        <div className="text-xs text-gray-500 mt-2">
                          {similarSites.length} sites within {radius} miles
                        </div>
                      </div>
                    </section>
                  )}

                  {loadingSim && (
                    <div className="flex items-center space-x-2 text-purple-600">
                      <FaSpinner className="animate-spin" />
                      <span>Searching…</span>
                    </div>
                  )}
                </div>
              </div>

              {/* results list */}
              {similarSites.length > 0 && (
                <section>
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                    Similar Sites ({similarSites.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-2">
                    {similarSites.map((p) => (
                      <div
                        key={p.place_id}
                        className="bg-gray-50 border rounded p-3 text-sm hover:bg-gray-100"
                      >
                        <a
                          href={`https://www.google.com/maps/place/?q=place_id:${p.place_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-purple-700 hover:underline flex items-center space-x-1"
                        >
                          <span>{p.name}</span>
                          <FaExternalLinkAlt className="text-xs" />
                        </a>
                        <div className="text-gray-600 mt-1">
                          {p.formatted_address}
                        </div>
                        <div className="flex items-center space-x-3 mt-2">
                          {p.website && (
                            <a
                              href={p.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline flex items-center space-x-1"
                            >
                              <FaGlobe className="text-xs" />
                              <span>
                                {new URL(p.website).hostname.replace(
                                  "www.",
                                  ""
                                )}
                              </span>
                            </a>
                          )}
                          {p.phone && (
                            <div className="text-xs text-gray-600 flex items-center space-x-1">
                              <FaPhone className="text-xs" />
                              <span>{p.phone}</span>
                            </div>
                          )}
                        </div>
                        {p.distance_m != null && (
                          <div className="text-xs text-gray-500 mt-1">
                            {Math.round(p.distance_m / 1609)} miles away
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Generators;
